import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// ===== DROP MODE CONFIG - Modifica qui =====
const DROP_CONFIG = {
  enabled: true,
  checkoutUrl: '/checkout',
  buttonText: 'VAI AL CHECKOUT',
  softLockSession: true,
  failSafeUnlockMs: 10000,
  successMessageMs: 4000,
  errorMessageMs: 8000,
  buttonReenableMs: 1200
};

const ANIMATION_DURATION_MS = 800;

/**
 * AddToCart ottimizzato con animazioni fluide
 */
class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];
  #resetTimeouts = [];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimeouts();
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  #clearTimeouts() {
    this.#resetTimeouts.forEach(clearTimeout);
    this.#resetTimeouts = [];
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const productForm = this.closest('product-form-component');
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart?.()) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) return;
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    preloadImage(this.dataset.productVariantMedia);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCart = document.createElement('fly-to-cart');
    const isQuickAdd = addToCartButton.classList.contains('quick-add__button');
    
    flyToCart.className = isQuickAdd ? 'fly-to-cart--quick' : 'fly-to-cart--main';
    flyToCart.style.backgroundImage = `url(${image})`;
    flyToCart.style.setProperty('--start-opacity', '0');
    flyToCart.source = addToCartButton;
    flyToCart.destination = cartIcon;

    document.body.appendChild(flyToCart);
  }

  async animateAddToCart() {
    const { addToCartButton } = this.refs;
    this.#clearTimeouts();

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) this.#resetTimeouts.splice(index, 1);
    }, ANIMATION_DURATION_MS);

    this.#resetTimeouts.push(timeoutId);
  }
}

/**
 * ProductFormComponent DROP MODE - Robusto & Veloce
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #isProcessing = false;
  #checkoutModeActive = false;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #activateCheckoutMode() {
    if (this.#checkoutModeActive) return;
    this.#checkoutModeActive = true;

    const buttons = this.querySelectorAll('add-to-cart-component');
    buttons.forEach(button => {
      const btn = button.refs.addToCartButton;
      if (!btn || btn.dataset.checkoutMode === 'true') return;

      if (!btn.dataset.originalText) {
        btn.dataset.originalText = btn.textContent?.trim() || '';
      }

      btn.disabled = false;
      btn.type = 'button';
      btn.dataset.checkoutMode = 'true';
      btn.textContent = DROP_CONFIG.buttonText;

      if (!btn.dataset.checkoutListenerAttached) {
        btn.dataset.checkoutListenerAttached = 'true';
        btn.addEventListener('click', () => {
          window.location.assign(DROP_CONFIG.checkoutUrl);
        }, { once: true });
      }
    });
  }

  #isVariantLocked(variantId) {
    if (!DROP_CONFIG.enabled || !DROP_CONFIG.softLockSession) return false;
    try {
      return sessionStorage.getItem(`atc_locked_${variantId}`) === '1';
    } catch {
      return false;
    }
  }

  #lockVariant(variantId) {
    try {
      sessionStorage.setItem(`atc_locked_${variantId}`, '1');
    } catch {}
  }

  async handleSubmit(event) {
    event.preventDefault();
    
    if (this.#isProcessing) return;
    
    const variantIdInput = this.querySelector('input[name="id"]');
    const variantId = variantIdInput?.value;
    if (!variantId) return;

    if (this.#isVariantLocked(variantId)) {
      this.#activateCheckoutMode();
      return;
    }

    const quantityValidation = this.refs.quantitySelector?.canAddToCart?.();
    if (quantityValidation && !quantityValidation.canAdd) {
      this.#showError(quantityValidation.message || 'Quantità non valida');
      return;
    }

    this.#isProcessing = true;
    this.#disableAllButtons();

    try {
      await this.#processAddToCart(variantId);
    } finally {
      this.#isProcessing = false;
      this.#cleanupProcessing();
    }
  }

  async #processAddToCart(variantId) {
    const form = this.querySelector('form');
    if (!form) throw new Error('Form mancante');

    const formData = new FormData(form);
    const cartSections = Array.from(document.querySelectorAll('cart-items-component'))
      .map(item => item.dataset.sectionId)
      .filter(Boolean);
    cartSections.forEach(section => formData.append('sections', section));

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('javascript', { body: formData }),
      headers: { Accept: 'application/json' }
    });

    const result = await response.json();

    if (result.status) {
      this.#showError(result.message || 'Errore aggiunta al carrello');
      this.dispatchEvent(new CartErrorEvent(this.id, result));
      return;
    }

    this.#lockVariant(variantId);
    this.#announceSuccess('Aggiunto al carrello');
    await this.#updateCartQuantity(variantId);

    this.dispatchEvent(new CartAddEvent({}, variantId, {
      source: 'product-form-component',
      success: true
    }));

    if (DROP_CONFIG.enabled) {
      this.#activateCheckoutMode();
    }
  }

  #disableAllButtons() {
    this.querySelectorAll('add-to-cart-component').forEach(btn => btn.disable());
  }

  #cleanupProcessing() {
    if (DROP_CONFIG.enabled) {
      this.#activateCheckoutMode();
    } else {
      setTimeout(() => {
        this.querySelectorAll('add-to-cart-component').forEach(btn => btn.enable());
      }, DROP_CONFIG.buttonReenableMs);
    }
  }

  #showError(message) {
    const errorEl = this.refs.addToCartTextError;
    if (!errorEl) return;

    errorEl.classList.remove('hidden');
    errorEl.textContent = message;
    this.refs.liveRegion.textContent = message;

    setTimeout(() => {
      errorEl.classList.add('hidden');
      this.refs.liveRegion.textContent = '';
    }, DROP_CONFIG.errorMessageMs);
  }

  #announceSuccess(message) {
    this.refs.liveRegion.textContent = message;
    setTimeout(() => {
      this.refs.liveRegion.textContent = '';
    }, DROP_CONFIG.successMessageMs);
  }

  async #updateCartQuantity(variantId) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const quantitySelector = this.querySelector('quantity-selector-component');
      if (quantitySelector?.setCartQuantity) {
        const cartItem = cart.items.find(item => 
          item.variant_id.toString() === variantId
        );
        quantitySelector.setCartQuantity(cartItem?.quantity || 0);
      }
    } catch (error) {
      console.warn('Cart update failed:', error);
    }
  }

  #onCartUpdate = async (event) => {
    if (event.detail?.sourceId === this.id) return;
    await this.#updateCartQuantity(event.detail?.resource?.id || '');
  };

  #onVariantUpdate = async (event) => {
    const variant = event.detail.resource;
    const variantId = variant?.id || '';

    this.refs.variantId.value = variantId;
    this.dataset.productId = event.detail.data.newProduct?.id || '';

    const buttonContainer = this.refs.addToCartButtonContainer;
    if (buttonContainer) {
      variant?.available ? buttonContainer.enable() : buttonContainer.disable();
    }

    if (variant?.featured_media?.preview_image?.src) {
      this.refs.addToCartButtonContainer?.setAttribute(
        'data-product-variant-media',
        variant.featured_media.preview_image.src + '&width=100'
      );
    }
  };

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

// REGISTRAZIONE
if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}
if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
